import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import * as api from './index';

// Query keys. Every read of an entity lives under one root — ['tasks'],
// ['accounts'] — so a write to that entity can invalidate every list, board,
// lookup and detail view of it in one call, wherever it is on screen.
export const keys = {
  entity: (name) => [name],
  list: (name, params) => [name, 'list', params || {}],
  board: (name, params) => [name, 'board', params || {}],
  detail: (name, id) => [name, 'detail', String(id)],
  lookup: (name) => [name, 'lookup'],
  dashboard: {
    stats: ['dashboard', 'stats'],
    widgets: ['dashboard', 'widgets'],
  },
};

// Which other caches a write to an entity should refresh. Tasks and
// activities feed the dashboard and appear on account, contact and
// opportunity pages; contacts and opportunities belong to an account.
const RELATED = {
  tasks: ['dashboard', 'today', 'accounts', 'opportunities', 'contacts'],
  activities: ['dashboard', 'today', 'accounts', 'opportunities', 'contacts'],
  contacts: ['accounts', 'dashboard'],
  opportunities: ['accounts', 'dashboard', 'today'],
  accounts: ['dashboard', 'today'],
  quotes: ['opportunities', 'dashboard'],
  orders: ['opportunities', 'dashboard'],
};

/** Refresh every cached view of `name` and of the entities that show it. */
export function invalidateEntity(queryClient, name) {
  const roots = [name, ...(RELATED[name] || [])];
  return Promise.all(roots.map((root) => queryClient.invalidateQueries({ queryKey: [root] })));
}

export function useInvalidate() {
  const queryClient = useQueryClient();
  return useCallback((name) => invalidateEntity(queryClient, name), [queryClient]);
}

const unwrap = (res) => res.data;
const asList = (res) => (Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.data) ? res.data.data : []));

// ---------------------------------------------------------------------------
// Reads

export const useDashboardStats = () =>
  useQuery({ queryKey: keys.dashboard.stats, queryFn: () => api.getDashboardStats().then(unwrap) });

// The report widgets are optional: the reports module may be hidden for this
// user, and the dashboard must still render.
export const useDashboardWidgets = () =>
  useQuery({ queryKey: keys.dashboard.widgets, queryFn: () => api.getReportDashboardWidgets().then(unwrap).catch(() => null) });

// Today: one request, keyed by the person's local day; refreshed by any write
// to action items, activities, accounts or opportunities.
export const useToday = (date) =>
  useQuery({ queryKey: ['today', date], queryFn: () => api.getToday(date).then(unwrap), placeholderData: keepPreviousData });

export const useActivities = (params) =>
  useQuery({ queryKey: keys.list('activities', params), queryFn: () => api.getActivities(params).then(asList), placeholderData: keepPreviousData });

// The kanban board wants the whole result set, unpaginated.
export const useTaskBoard = (params, enabled = true) =>
  useQuery({ queryKey: keys.board('tasks', params), queryFn: () => api.getTasks(params).then(asList), enabled, placeholderData: keepPreviousData });

// Lookups: whole reference lists for typeaheads and filters. A hidden module
// degrades to an empty list, never an error (api.optional). They change
// rarely, so they stay fresh for five minutes — and a write to the entity
// still invalidates them, so a new account appears in typeaheads at once.
const LOOKUP_STALE = 5 * 60 * 1000;
const useLookup = (name, fn, enabled = true) =>
  useQuery({ queryKey: keys.lookup(name), queryFn: () => api.optional(fn()).then(asList), staleTime: LOOKUP_STALE, enabled });

export const useAccountsLookup = (enabled = true) => useLookup('accounts', () => api.getAccounts(), enabled);
export const useContactsLookup = (enabled = true) => useLookup('contacts', () => api.getContacts({ limit: 1000 }), enabled);
export const useOpportunitiesLookup = (enabled = true) => useLookup('opportunities', () => api.getOpportunities(), enabled);
export const useUsersLookup = (enabled = true) =>
  useQuery({ queryKey: keys.lookup('users'), queryFn: () => api.getUsers().then((r) => r.data.filter((u) => u.active)), staleTime: LOOKUP_STALE, enabled });

// ---------------------------------------------------------------------------
// Writes
//
// One shape for every mutation: run the API call, then refresh the entity
// family. The caller's own onSuccess still runs, after the refresh, so what
// it reads next is current.

export function useEntityMutation(name, mutationFn, options = {}) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation({
    mutationFn,
    ...rest,
    onSuccess: async (data, variables, context) => {
      await invalidateEntity(queryClient, name);
      if (onSuccess) onSuccess(data, variables, context);
    },
  });
}

export const useCreateTask = (o) => useEntityMutation('tasks', (data) => api.createTask(data), o);
export const useCompleteTask = (o) => useEntityMutation('tasks', (id) => api.completeTask(id), o);
export const useReopenTask = (o) => useEntityMutation('tasks', (id) => api.reopenTask(id), o);
export const useDeleteTask = (o) => useEntityMutation('tasks', (id) => api.deleteTask(id), o);
export const usePatchTask = (o) => useEntityMutation('tasks', ({ id, patch }) => api.patchTask(id, patch), o);
export const useBulkUpdateTasks = (o) => useEntityMutation('tasks', ({ ids, updates }) => api.bulkUpdateTasks(ids, updates), o);
export const useUpdateTask = (o) => useEntityMutation('tasks', ({ id, data }) => api.updateTask(id, data), o);

export const usePatchOpportunity = (o) => useEntityMutation('opportunities', ({ id, patch }) => api.patchOpportunity(id, patch), o);
export const useTodayPreferences = (o) => useEntityMutation('today', (muted) => api.saveTodayPreferences(muted), o);
export const useTodaySettings = (o) => useEntityMutation('today', (quietDays) => api.updateTodaySettings(quietDays), o);

export const useCreateActivity = (o) => useEntityMutation('activities', (data) => api.createActivity(data), o);
export const useUpdateActivity = (o) => useEntityMutation('activities', ({ id, data }) => api.updateActivity(id, data), o);
export const useDeleteActivity = (o) => useEntityMutation('activities', (id) => api.deleteActivity(id), o);

export const useCreateAccount = (o) => useEntityMutation('accounts', (data) => api.createAccount(data), o);
export const useCreateContact = (o) => useEntityMutation('contacts', (data) => api.createContact(data), o);

/** The message an API error should show, or the fallback. */
export const errorMessage = (err, fallback) => (err && err.response && err.response.data && err.response.data.error) || fallback;
